/**
 * Various polyfills
 * NOTE: import order is important here so this must be the first
 * import in this file.
 */
import "./app-polyfills";

import * as React from "react";

import { AnalyticsProvider } from "@accurx/analytics";
import { QueryClientProvider } from "@accurx/api/QueryClient";
import { AuthProvider } from "@accurx/auth";
import { BaseStyles } from "@accurx/design";
import { getEmbedMode } from "@accurx/native";
import { AppSettings, DateHelpers, ErrorBoundary, Log } from "@accurx/shared";
import * as Sentry from "@sentry/react";
import { BaseApp } from "BaseApp";
import { OptionalRealtimeProvider } from "OptionalRealtimeProvider";
import { configureHttpClient2 } from "configureHttpClient2";
import { createBrowserHistory } from "history";
import { setupLoggingProviders } from "logging";
import { setupReAuthFlow } from "reAuth";
import * as ReactDOM from "react-dom";
import { Provider } from "react-redux";
import { Router } from "react-router-dom";

import { VersionChecker } from "app/VersionChecker";
import { appConfigureStore } from "store/configureStore";

// dynamically update the favicon if we're not on production
(function setFavicon() {
    const host = window.location.hostname;
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "icon");
        document.head.appendChild(link);
    }
    switch (true) {
        case /localhost$/.test(host):
            link.setAttribute("href", "/favicon-local.ico");
            break;
        case /\.dev\.accurx\.com$/.test(host):
            link.setAttribute("href", "/favicon-dev.ico");
            break;
        case /\.demo\.accurx\.com$/.test(host):
            link.setAttribute("href", "/favicon-demo.ico");
            break;
        default:
            link.setAttribute("href", "/favicon.ico");
            break;
    }
})();

/**
 * Get a PR number from the query params and set it in AppSettings.
 * This is use to point directly at PRs eg: https://web.dev.accurx.com/?AccurxPrNumber=123
 *
 * NOTE - this is currently duplicated in florey client. Once stable we can share code in a better way
 */
(function configureHeaderBasedRouting() {
    const prNumberKey = "AccurxPrNumber";

    // safely handle the AccurxPrNumber query param
    try {
        const prNumber = readPrNumberFromUrl(prNumberKey);
        const logTags = { tags: { pr: prNumber } };

        // store pr number for use throughout rest of the session
        AppSettings.accurxPrNumber = prNumber;

        let prevUrl = window.location.href;
        new MutationObserver((mutations) => {
            // check URL after page mutation and add AccurxPrNumber if needed
            try {
                const currentUrl = window.location.href;
                if (currentUrl !== prevUrl) {
                    const updated = createUrlWithPrNumberParam(
                        currentUrl,
                        prNumberKey,
                    );

                    if (updated) {
                        const history = window.history;
                        history.replaceState(history.state, "", updated);
                        prevUrl = updated.toString();

                        Log.info(
                            `AccurxPrNumber: updated URL with ${prNumberKey} param`,
                            logTags,
                        );
                    }
                }
            } catch (err) {
                Log.error(`Error adding ${prNumberKey}=${prNumber} to URL`, {
                    originalException: err,
                });
            }

            // update all links with AccurxPrNumber param
            const links = document.querySelectorAll("a");
            let updatedLinkCount = 0;

            links.forEach((link) => {
                try {
                    if (!link.href) return;

                    const updated = createUrlWithPrNumberParam(
                        link.href,
                        prNumberKey,
                    );

                    if (!updated) return;

                    link.href =
                        window.location.hostname === updated.hostname
                            ? updated.pathname + updated.search
                            : updated.href;

                    updatedLinkCount += 1;
                } catch (err) {
                    Log.error(
                        `Error adding ${prNumberKey}=${prNumber} to href=${link.href}`,
                        {
                            originalException: err,
                        },
                    );
                }
            });

            if (updatedLinkCount > 0) {
                const msg = `AccurxPrNumber: updated ${updatedLinkCount} ${
                    updatedLinkCount === 1 ? "href" : "hrefs"
                } with ${prNumberKey} param`;
                Log.info(msg, logTags);
            }
        }).observe(document, { subtree: true, childList: true });
    } catch (err) {
        Log.error(`Error encountered handling ${prNumberKey} query param`, {
            originalException: err,
        });
    }

    function readPrNumberFromUrl(paramName: string) {
        return ["local", "development"].includes(AppSettings.accurxEnvironment)
            ? new URLSearchParams(window.location.search).get(paramName)
            : null;
    }

    function createUrlWithPrNumberParam(href: string, paramName: string) {
        const pr = AppSettings.accurxPrNumber;
        // no PR number to add
        if (pr === null) return;
        // query string already has AccurxPrNumber in it
        if (href.indexOf(paramName) > -1) return;

        const updatedUrl = new URL(href);

        pr.length > 0
            ? updatedUrl.searchParams.set(paramName, pr)
            : updatedUrl.searchParams.delete(paramName);

        return updatedUrl;
    }
})();

// application entrypoint
main();

function main() {
    DateHelpers.setDefaultMomentTimezone();
    DateHelpers.setMomentLocale();

    // Create browser history to use in the Redux store
    const baseUrl = document
        .getElementsByTagName("base")[0]
        .getAttribute("href") as string;

    const history = createBrowserHistory({ basename: baseUrl });

    setupLoggingProviders(history);
    configureHttpClient2();

    // Get the application-wide store instance, prepopulating with state from the server where available.
    const store = appConfigureStore();

    const embedMode = getEmbedMode();

    const render = (AppComponent: React.ComponentType) => {
        ReactDOM.render(
            <ErrorBoundary>
                <QueryClientProvider>
                    <Provider store={store}>
                        <BaseStyles />
                        <Router history={history}>
                            <AuthProvider
                                isDesktopUser={embedMode === "Desktop"}
                            >
                                <OptionalRealtimeProvider>
                                    <AnalyticsProvider>
                                        <VersionChecker>
                                            <AppComponent />
                                        </VersionChecker>
                                    </AnalyticsProvider>
                                </OptionalRealtimeProvider>
                            </AuthProvider>
                        </Router>
                    </Provider>
                </QueryClientProvider>
            </ErrorBoundary>,
            document.getElementById("root"),
        );
    };

    // wrap with Sentry profiler, for pageload transactions
    render(Sentry.withProfiler(BaseApp));

    setupReAuthFlow();
}
